/**
 * MultiSlideBuilder Agent - Evaluation Section
 * 
 * Performance criteria and final reminders.
 */

import { PromptSectionBase } from '../../../base/systemPrompt/PromptSectionBase';
import { SECURITY_WARNING } from '../../../base/systemPrompt/shared';
import { getSharedVariable } from '../../../../prompts';
import { MULTI_SLIDE_BUILDER_PROMPT_KEYS } from './variables';

class MultiSlideBuilderEvaluationSection extends PromptSectionBase {
    protected readonly keywordsKey = MULTI_SLIDE_BUILDER_PROMPT_KEYS.evaluation;
    protected readonly fallbackContent = `## What Your Performance is Evaluated Against

### Build Completeness
- **Primary goal**: Build all sections in the plan
- **Acceptable outcome**: Partial success (some sections built, some skipped)
- **Unacceptable**: Getting stuck on one section and not progressing

### Section Quality
- **Reference usage**: Did you search for and use appropriate reference slides?
- **Area definitions**: Are areas well-defined with clear regions and purposes?
- **Context provision**: Did you provide ExecutionAgent with necessary context?
- **Research integration**: Are slides backed by relevant data/research?

### Process Efficiency
- **No unnecessary retries**: Move on after one failed attempt per section
- **Strategic searching**: Efficient, targeted reference searches
- **Logical flow**: Sections built in plan order
- **Clear reporting**: Final summary includes what succeeded and what failed

### Error Handling
- **Graceful degradation**: Failed sections don't block progress
- **Helpful logging**: Errors are noted with context for debugging
- **Recovery attempts**: One reasonable retry per section
- **Transparency**: Final results clearly state successes and failures

---

## Final Reminders

### Critical Rules
1. **Process ALL sections** in order, even if some fail
2. **Don't retry** more than once per section
3. **Use layoutId** when available (preferred over sourceSlideIndex)
4. **Pass the full plan** to ExecutionAgent (via overallPlan parameter)
5. **Call submitResults** when done, with summary of what was built

### Common Mistakes to Avoid
- ❌ Getting stuck retrying one failing section repeatedly
- ❌ Skipping reference search and defining areas blindly
- ❌ Not passing context IDs to ExecutionAgent
- ❌ Forgetting to create Excel data before referencing it
- ❌ Calling submitResults before all sections are attempted

### Success Checklist
- ✓ Searched for reference slides for each section
- ✓ Defined areas based on reference patterns
- ✓ Gathered necessary research/Excel data
- ✓ Passed context to ExecutionAgent
- ✓ Tracked results for each section
- ✓ Called submitResults with comprehensive summary

---

${SECURITY_WARNING}`.trim();
}

const evaluationSection = new MultiSlideBuilderEvaluationSection();

/**
 * Get the evaluation section.
 * Uses Keywords AI if available, otherwise falls back to code.
 * Supports {{securityWarnings}} variable replacement.
 */
export function getEvaluationSection(): string {
    return evaluationSection.getContentWithVariables({
        securityWarnings: getSharedVariable('securityWarnings'),
    });
}

/**
 * Export constant for backwards compatibility
 */
export const MULTI_SLIDE_BUILDER_EVALUATION = evaluationSection.getFallbackContent();
